import { useEffect } from "react";
import { Link, Outlet } from "react-router-dom";

import { socket, startSocket } from "./api/socket";
import { ALL_MARKETS } from "./api/types";
import { ConnectionBadge } from "./components/ConnectionBadge";
import { HotkeyHelp } from "./components/HotkeyHelp";
import { AccountKillSwitches, KillSwitch, KillSwitchBanner } from "./components/KillSwitch";
import { SimBanner } from "./components/SimSwitch";
import { OpsAlerts } from "./components/OpsAlerts";
import { SignalAlerts } from "./components/SignalAlerts";
import { StrategyAlerts } from "./components/StrategyAlerts";
import { SignalSoundToggle } from "./components/SignalSoundToggle";
import { useParticipantsRefreshOnFlow } from "./hooks/useParticipants";
import { useTimelineRefreshOnFlow } from "./hooks/useTimeline";
import { usePositionsRefreshOnFill } from "./hooks/usePositions";
import { startHotkeys } from "./store/hotkeys";
import { startPendingWatch } from "./store/useOrders";
import { startAccountsWatch } from "./store/useAccounts";

/**
 * Макет приложения: шапка и место под страницу.
 *
 * Сокет запускается здесь, а не в провайдере: он ресурс уровня вкладки, и
 * закрывать его при размонтировании макета нечего. Обе операции идемпотентны,
 * поэтому двойной вызов эффекта в StrictMode безвреден.
 */
export default function App() {
  // Перечитывание позиций по факту ИСПОЛНЕНИЯ — ресурс уровня вкладки, а не
  // страницы: филл может приехать по рынку, который сейчас не открыт (оператор
  // ушёл на следующее окно серии, а ордер прошлого ещё стоял).
  usePositionsRefreshOnFill();
  useParticipantsRefreshOnFlow();
  // Ряд накопления будится тем же стуком и тоже на уровне вкладки: подписка,
  // живущая вместе с панелью, теряла бы стуки на её перемонтировании.
  useTimelineRefreshOnFlow();

  useEffect(() => {
    startSocket();
    // Общая лента удерживается всё время жизни вкладки и намеренно не
    // освобождается: её объём ограничен стримом evt.tape (нормализованные
    // сделки, на порядки реже дельт стакана), а история в 200 событий приходит
    // при каждом подключении и наполняет ленту и на дашборде, и на странице
    // рынка. Стаканы по этой подписке не идут — web_api их «*» не отдаёт.
    socket.retain(ALL_MARKETS);
    // Тикер, помечающий команды без ответа. Один на вкладку, как и сокет.
    startPendingWatch();
    // Состав аккаунтов — тоже уровня вкладки: его читают выбор цели, столбцы
    // ордеров и ленты и кнопки команд.
    startAccountsWatch();
    // Слушатель горячих клавиш — тоже ресурс уровня вкладки. Смонтированный
    // вместе с компонентом, он течёт в StrictMode и гоняется с фокусом.
    // Приёмник действий ставит страница рынка; здесь только «?» и Esc.
    return startHotkeys();
  }, []);

  return (
    <div className="flex min-h-full flex-col">
      <header className="flex items-center justify-between border-b border-[var(--color-border)] px-6 py-3">
        <div className="flex items-baseline gap-3">
          <Link to="/" className="text-lg font-semibold tracking-tight">
            pm_scalper
          </Link>
          <span className="text-xs text-[var(--color-muted)]">торговое окно оператора</span>
          {/* Ссылка в шапке, а не на дашборде: история — единственное место, где
              контрагент виден дольше двух минут, и искать её по страницам
              рынков значило бы вспоминать, на каком именно рынке был филл. */}
          <Link
            to="/trades"
            className="text-xs text-[var(--color-muted)] hover:text-white"
            title="история наших сделок и контрагентов"
          >
            мои сделки
          </Link>
          {/* Рядом с историей, а не вместо неё: там строка на СДЕЛКУ, здесь на
              КОШЕЛЁК. «Кто принял эту тысячу» и «кто берёт у меня каждый день» —
              разные вопросы, и свести первый во второй глазами нельзя. */}
          <Link
            to="/counterparties"
            className="text-xs text-[var(--color-muted)] hover:text-white"
            title="кто принимает наши ставки: свод встреч по кошелькам"
          >
            контрагенты
          </Link>
          <Link
            to="/rebates"
            className="text-xs text-[var(--color-muted)] hover:text-white"
            title="тир программы возврата тейкерской комиссии: сколько набрано и удержится ли ступень"
          >
            возврат
          </Link>
          <Link
            to="/signals"
            className="text-xs text-[var(--color-muted)] hover:text-white"
            title="копирование ставок умных кошельков: сводка гипотез и лента сигналов"
          >
            сигналы
          </Link>
        </div>
        <div className="flex items-center gap-4">
          <SignalSoundToggle />
          <KillSwitch />
          <AccountKillSwitches />
          <ConnectionBadge />
        </div>
      </header>

      <KillSwitchBanner />
      {/* Полоса симуляции — ПОД полосой стопа: стоп важнее, и порядок полос
          обязан это повторять. Обе видны одновременно и не спорят цветом. */}
      <SimBanner />
      {/* Операционные тревоги (веха M8) — ВЫШЕ торговых карточек, но ниже
          стопа. Пока терминал не видит рынка, торговые предложения по нему не
          стоят ничего; но вытеснять стоп они не вправе — его положение решает,
          уйдут ли деньги вообще. Это НЕ торговая карточка: сделки здесь не
          предлагают, здесь сообщают о поломке стека. */}
      <OpsAlerts />
      {/* Карточки сигналов — в МАКЕТЕ, а не на странице сигналов: окна серии
          закрываются каждые пять минут, и сигнал приходит по тому окну, на
          которое оператор в этот момент почти наверняка не смотрит. */}
      <SignalAlerts />
      {/* Карточки стратегий — рядом с карточками китов и по тому же доводу:
          строка ступени `alert` рождается за 17–46 секунд до конца своего окна, и
          смотреть в этот момент на её панель некому.

          Здесь стояли карточки ЗАМКА. Их сняли по решению оператора: полоса
          сквозная, две очереди предложений соревновались бы за один взгляд, а их
          звонки были бы неразличимы на слух. Замок остался подсистемой и остался
          в «Сверке индекса» — вернуть ему полосу можно этой же строкой. */}
      <StrategyAlerts />
      <HotkeyHelp />

      <main className="flex-1 p-6">
        <Outlet />
      </main>
    </div>
  );
}
